import React, { useState, useEffect, useMemo } from "react";
import {
  ItemMargin,
  UpdateItemParams,
  MarginType,
  MarginTypeEnum,
  CostingMethod,
  CostingMethodEnum,
  CalculateCostsResponseSchema,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Loader } from "lucide-react";
import { createId } from "@paralleldrive/cuid2";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchApi } from "@/lib/api";
import Decimal from "decimal.js";
import { MarginInputRow } from "./MarginInputRow";
import { sumMargins } from "./margins";

interface BOMMarginEditorProps {
  itemId: string;
  margins: ItemMargin[];
  quantities: string[];
  costingMethod: CostingMethod;
  onUpdate: (updates: UpdateItemParams) => Promise<true | string>;
  scrollRef?: (el: HTMLDivElement | null) => void;
  onScrollSync?: React.UIEventHandler<HTMLDivElement>;
}

interface MarginEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  margins: ItemMargin[];
  costingMethod: CostingMethod;
  onSave: (margins: ItemMargin[]) => Promise<void>;
}

interface LocalMargin {
  id: string;
  type: "PERCENTAGE" | "AMOUNT";
  value: string;
  marginType: MarginType;
  minQty?: number;
  isQuantityOverride: boolean;
}

interface MarginPriceData {
  quantity: string;
  unitCost: string;
  unitPrice: string;
  marginsByType?: {
    operations?: string;
    outsideOperations?: string;
    purchased?: string;
    material?: string;
    manufactured?: string;
    all?: string;
  };
}

/**
 * Returns the available margin types based on the costing method.
 * BOM items can have specific margin types for each cost category.
 * UNIT_COST items can only use the "all" margin type.
 * @param costingMethod - The costing method of the item
 * @returns Array of available margin types
 */
function getAvailableMarginTypes(costingMethod: CostingMethod): MarginType[] {
  if (costingMethod === CostingMethodEnum.BOM) {
    return [
      MarginTypeEnum.purchased,
      MarginTypeEnum.operations,
      MarginTypeEnum.outsideOperations,
      MarginTypeEnum.material,
      MarginTypeEnum.manufactured,
      MarginTypeEnum.all,
    ];
  }
  return [MarginTypeEnum.all];
}

/**
 * Formats a margin type for display. (Capitalizes the first letter)
 */
function formatMarginType(marginType: MarginType): string {
  if (marginType === "outsideOperations") {
    return "Outside Operations";
  }
  if (marginType === "operations") {
    return "Inside Operations";
  }
  if (marginType === "manufactured") {
    return "Manufactured";
  }
  return marginType.charAt(0).toUpperCase() + marginType.slice(1);
}

/**
 * Determines which margin rule is actually being applied for a specific quantity.
 * Mirrors backend logic from getApplicableMargin.
 * Used to highlight active margins in blue in the UI by comparing if the current
 * row's margin matches the returned margin for that quantity.
 */
function getActiveMarginForQuantity(
  margins: ItemMargin[],
  type: MarginType,
  qty: number,
): ItemMargin | undefined {
  const marginsForType = margins.filter((m) => m.type === type);

  if (marginsForType.length === 0) {
    return undefined;
  }

  const applicableQuantityMargins = marginsForType.filter(
    (m) => m.minQty !== undefined && m.minQty <= qty,
  );

  if (applicableQuantityMargins.length > 0) {
    return applicableQuantityMargins.reduce((prev, current) =>
      (current.minQty ?? 0) > (prev.minQty ?? 0) ? current : prev,
    );
  }

  const defaultMargin = marginsForType.find((m) => m.minQty === undefined);
  if (defaultMargin) {
    return defaultMargin;
  }

  return undefined;
}

interface MarginTypeGroup {
  marginType: MarginType;
  default?: LocalMargin;
  overrides: LocalMargin[];
}

function MarginEditorSheet({
  open,
  onOpenChange,
  margins,
  costingMethod,
  onSave,
}: MarginEditorSheetProps) {
  const [localMargins, setLocalMargins] = useState<LocalMargin[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const availableMarginTypes = getAvailableMarginTypes(costingMethod);

  const handleOpenChange = (newOpen: boolean) => {
    if (isSaving) {
      return;
    }
    onOpenChange(newOpen);
  };

  useEffect(() => {
    if (open) {
      const converted = margins.map((m) => ({
        id: createId(),
        type: m.marginAmount.type,
        value:
          m.marginAmount.type === "PERCENTAGE"
            ? m.marginAmount.percentage || "0"
            : m.marginAmount.amount || "0",
        marginType: m.type,
        minQty: m.minQty,
        isQuantityOverride: m.minQty !== undefined,
      }));
      setLocalMargins(converted.length > 0 ? converted : []);
      setValidationError(null);
    }
  }, [open, margins]);

  const marginTypeGroups = useMemo((): MarginTypeGroup[] => {
    const groups = new Map<MarginType, MarginTypeGroup>();

    for (const margin of localMargins) {
      if (!groups.has(margin.marginType)) {
        groups.set(margin.marginType, {
          marginType: margin.marginType,
          overrides: [],
        });
      }

      const group = groups.get(margin.marginType)!;
      if (margin.isQuantityOverride) {
        group.overrides.push(margin);
      } else {
        group.default = margin;
      }
    }

    return Array.from(groups.values());
  }, [localMargins]);

  const usedMarginTypes = new Set(marginTypeGroups.map((g) => g.marginType));
  const unusedMarginTypes = availableMarginTypes.filter(
    (type) => !usedMarginTypes.has(type),
  );

  const handleAddMarginType = (marginType: MarginType) => {
    const newMargin: LocalMargin = {
      id: createId(),
      type: "PERCENTAGE",
      value: "0",
      marginType,
      isQuantityOverride: false,
    };
    setLocalMargins([...localMargins, newMargin]);
  };

  const handleAddQuantityMargin = (marginType: MarginType) => {
    const existingForType = localMargins.filter(
      (m) => m.marginType === marginType,
    );
    const maxQty = Math.max(
      0,
      ...existingForType
        .map((m) => m.minQty)
        .filter((q): q is number => q !== undefined),
    );

    const newMargin: LocalMargin = {
      id: createId(),
      type: "PERCENTAGE",
      value: "0",
      marginType,
      minQty: maxQty + 10,
      isQuantityOverride: true,
    };
    setLocalMargins([...localMargins, newMargin]);
  };

  const handleRemoveMargin = (id: string) => {
    setLocalMargins(localMargins.filter((m) => m.id !== id));
  };

  const handleRemoveMarginType = (marginType: MarginType) => {
    setLocalMargins(localMargins.filter((m) => m.marginType !== marginType));
  };

  const handleTypeChange = (id: string, type: "PERCENTAGE" | "AMOUNT") => {
    setLocalMargins(
      localMargins.map((m) => (m.id === id ? { ...m, type, value: "0" } : m)),
    );
  };

  const handleValueChange = (id: string, value: string) => {
    setLocalMargins(
      localMargins.map((m) => (m.id === id ? { ...m, value } : m)),
    );
  };

  const handleMinQtyChange = (id: string, minQty?: number) => {
    setLocalMargins(
      localMargins.map((m) =>
        m.id === id
          ? { ...m, minQty, isQuantityOverride: minQty !== undefined }
          : m,
      ),
    );
  };

  /**
   * Validates margin configurations before saving.
   * Ensures:
   * 1. Quantity overrides have a minQty value set
   * 2. No duplicate margins exist for the same margin type + quantity combination
   * 3. Percentage margins are less than 100%
   * Returns an error message string if validation fails, or null if valid.
   */
  const validateMargins = (margins: LocalMargin[]): string | null => {
    const seen = new Set<string>();

    for (const margin of margins) {
      if (margin.isQuantityOverride && margin.minQty === undefined) {
        return `Quantity override for ${formatMarginType(margin.marginType)} must have a quantity value`;
      }

      const key = `${margin.marginType}:${margin.minQty ?? "default"}`;

      if (seen.has(key)) {
        return margin.minQty !== undefined
          ? `Duplicate margin for ${formatMarginType(margin.marginType)} at quantity ${margin.minQty}`
          : `Duplicate default margin for ${formatMarginType(margin.marginType)}`;
      }
      seen.add(key);

      if (margin.type === "PERCENTAGE") {
        const percentage = new Decimal(margin.value || "0");
        if (percentage.greaterThanOrEqualTo(100)) {
          return `Margin percentage must be less than 100% for ${formatMarginType(margin.marginType)}. Got: ${percentage.toString()}%`;
        }
      }
    }

    return null;
  };

  const handleSave = async () => {
    const filteredMargins = localMargins.filter(
      (m) => m.value && m.value !== "0",
    );

    const validationErrorMsg = validateMargins(filteredMargins);
    if (validationErrorMsg) {
      setValidationError(validationErrorMsg);
      return;
    }

    setIsSaving(true);
    setValidationError(null);

    const newMargins: ItemMargin[] = filteredMargins.map((m) => ({
      type: m.marginType,
      minQty: m.minQty,
      marginAmount:
        m.type === "PERCENTAGE"
          ? { type: "PERCENTAGE" as const, percentage: m.value }
          : { type: "AMOUNT" as const, amount: m.value },
    }));

    await onSave(newMargins);
    setIsSaving(false);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-[600px] flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle>Edit Margins</SheetTitle>
          <SheetDescription>
            {costingMethod === CostingMethodEnum.BOM
              ? "Purchased, Material, and Manufactured margins only apply to items with a Unit Cost costing method. All applies to every item, including those with a BOM costing method."
              : "Configure a margin applied to the item's unit cost."}
          </SheetDescription>
          {validationError && (
            <div className="bg-destructive/10 border border-destructive/30 text-destructive px-3 py-2 rounded-md text-sm">
              {validationError}
            </div>
          )}
        </SheetHeader>

        <div className="flex justify-between items-center py-4 flex-shrink-0 border-b">
          <Label>Margins</Label>
          {unusedMarginTypes.length > 0 && (
            <Select
              value=""
              onValueChange={(value: MarginType) => handleAddMarginType(value)}
              disabled={isSaving}
            >
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue placeholder="Add Margin Type" />
              </SelectTrigger>
              <SelectContent>
                {unusedMarginTypes.map((marginType) => (
                  <SelectItem key={marginType} value={marginType}>
                    {formatMarginType(marginType)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="grid gap-4 py-4 flex-1 overflow-y-auto">
          {marginTypeGroups.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No margins configured. Select a margin type above to get started.
            </p>
          ) : (
            <div className="space-y-4">
              {marginTypeGroups.map((group) => (
                <div
                  key={group.marginType}
                  className="border rounded-lg p-4 space-y-3"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-semibold">
                        {formatMarginType(group.marginType)}
                      </h3>
                      {group.marginType === MarginTypeEnum.all &&
                        costingMethod === CostingMethodEnum.BOM && (
                          <p className="text-xs text-muted-foreground">
                            Applies to all items, including BOM
                          </p>
                        )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMarginType(group.marginType)}
                      disabled={isSaving}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {group.default && (
                    <MarginInputRow
                      isDefault={true}
                      marginType={group.default.type}
                      value={group.default.value}
                      onMarginTypeChange={(type) =>
                        handleTypeChange(group.default!.id, type)
                      }
                      onValueChange={(value) =>
                        handleValueChange(group.default!.id, value)
                      }
                      disabled={isSaving}
                    />
                  )}

                  {group.overrides.length > 0 && (
                    <div className="space-y-2 pl-4 border-l-2 border-border">
                      <span className="text-xs font-medium text-muted-foreground">
                        Quantity Overrides
                      </span>
                      {group.overrides.map((override) => (
                        <MarginInputRow
                          key={override.id}
                          isDefault={false}
                          minQty={override.minQty}
                          marginType={override.type}
                          value={override.value}
                          onMinQtyChange={(qty) =>
                            handleMinQtyChange(override.id, qty)
                          }
                          onMarginTypeChange={(type) =>
                            handleTypeChange(override.id, type)
                          }
                          onValueChange={(value) =>
                            handleValueChange(override.id, value)
                          }
                          onRemove={() => handleRemoveMargin(override.id)}
                          disabled={isSaving}
                          showQuantityInput={true}
                        />
                      ))}
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddQuantityMargin(group.marginType)}
                    disabled={isSaving}
                    className="w-full"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Quantity Override
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <SheetFooter className="flex-shrink-0">
          <SheetClose asChild>
            <Button variant="outline" disabled={isSaving}>
              Cancel
            </Button>
          </SheetClose>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader className="mr-2 h-4 w-4 animate-spin" />}
            Save Margins
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Component for editing BOM item margins with support for different margin types.
 * Displays a table showing margin configurations per quantity and allows editing via a sheet.
 * Used for MANUFACTURED items with detailed cost calculations.
 */
export function BOMMarginEditor({
  itemId,
  margins,
  quantities,
  costingMethod,
  onUpdate,
  scrollRef,
  onScrollSync,
}: BOMMarginEditorProps) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [priceData, setPriceData] = useState<MarginPriceData[]>([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);

  const handleSaveMargins = async (newMargins: ItemMargin[]) => {
    await onUpdate({ margin: newMargins });
  };

  useEffect(() => {
    const fetchPrices = async () => {
      if (!itemId) {
        return;
      }

      setIsLoadingPrices(true);
      try {
        const response = await fetchApi(
          `items/${itemId}/calculate-costs`,
          "POST",
          { body: { quantities } },
        );

        if (response.ok && response.data) {
          const { costs } = CalculateCostsResponseSchema.parse(response.data);
          setPriceData(
            costs.map((cost) => ({
              quantity: cost.quantity,
              unitCost: cost.unitCost,
              unitPrice: cost.unitPrice,
              marginsByType: cost.marginsByType,
            })),
          );
        }
      } catch {
        setPriceData([]);
      } finally {
        setIsLoadingPrices(false);
      }
    };

    fetchPrices();
  }, [itemId, margins, quantities]);

  /** One row per margin type (not per margin rule). */
  const marginTypeRows = useMemo(() => {
    const types = new Set(margins.map((m) => m.type));
    return Array.from(types).map((type) => ({
      type,
      label:
        getAvailableMarginTypes(costingMethod).length > 1
          ? formatMarginType(type)
          : "Margin",
    }));
  }, [margins, costingMethod]);

  return (
    <>
      <div
        ref={scrollRef}
        onScroll={onScrollSync}
        className="bg-card rounded-lg border shadow-sm overflow-x-auto"
      >
        <table
          className="w-full border-separate border-spacing-0 table-fixed"
          style={{ minWidth: `${300 + quantities.length * 120}px` }}
        >
          <thead className="bg-muted">
            <tr>
              <th className="text-left py-2 px-4 text-sm font-semibold w-[300px] min-w-[300px] sticky left-0 z-20 bg-muted border-r border-border border-b border-b-border">
                <div className="flex items-center justify-between">
                  <span>Margins</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsSheetOpen(true)}
                  >
                    Edit
                  </Button>
                </div>
              </th>
              {quantities.map((qty) => (
                <th
                  key={qty}
                  className="text-right py-2 px-4 text-sm font-semibold min-w-[100px] border-l border-border border-b border-b-border"
                >
                  Qty {qty}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {marginTypeRows.length === 0 ? (
              <tr>
                <td className="py-2 px-4 text-sm text-muted-foreground sticky left-0 z-10 bg-card border-r border-border border-b border-b-border">
                  No margins configured
                </td>
                {quantities.map((qty) => (
                  <td
                    key={qty}
                    className="py-2 px-4 text-right text-sm border-l border-border border-b border-b-border text-muted-foreground"
                  >
                    —
                  </td>
                ))}
              </tr>
            ) : (
              marginTypeRows.map((row) => (
                <tr key={row.type}>
                  <td className="py-2 px-4 text-sm sticky left-0 z-10 bg-card border-r border-border border-b border-b-border">
                    {row.label}
                  </td>
                  {quantities.map((qty) => {
                    const qtyNum = parseInt(qty, 10);
                    const costForQty = priceData.find(
                      (p) => p.quantity === qty,
                    );

                    const activeMargin = getActiveMarginForQuantity(
                      margins,
                      row.type,
                      qtyNum,
                    );

                    const activeRate =
                      activeMargin?.marginAmount.type === "PERCENTAGE"
                        ? `${activeMargin.marginAmount.percentage}%`
                        : activeMargin?.marginAmount.type === "AMOUNT"
                          ? `$${activeMargin.marginAmount.amount}`
                          : null;

                    const unitMargin =
                      costForQty && !isLoadingPrices && costForQty.marginsByType
                        ? (costForQty.marginsByType[row.type] ?? null)
                        : null;

                    return (
                      <td
                        key={qty}
                        className="py-2 px-4 text-right text-sm border-l border-border border-b border-b-border tabular-nums"
                      >
                        {isLoadingPrices ? (
                          <Loader className="h-4 w-4 animate-spin inline" />
                        ) : unitMargin !== null ? (
                          <div className="flex flex-col items-end">
                            <span>
                              $
                              {parseFloat(unitMargin).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              /ea
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              $
                              {new Decimal(unitMargin)
                                .times(qty)
                                .toNumber()
                                .toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                            </span>
                            {activeRate && (
                              <span className="text-[10px] text-muted-foreground italic">
                                ({activeRate})
                              </span>
                            )}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}

            {/* Total Margin Row */}
            {marginTypeRows.length > 0 && (
              <tr className="bg-accent font-semibold">
                <td className="py-2 px-4 text-sm sticky left-0 z-10 bg-accent border-r border-border border-t-2 border-t-border">
                  Total Margin
                </td>
                {quantities.map((qty) => {
                  const costForQty = priceData.find((p) => p.quantity === qty);

                  if (isLoadingPrices) {
                    return (
                      <td
                        key={qty}
                        className="py-2 px-4 text-right border-l border-border border-t-2 border-t-border"
                      >
                        <Loader className="h-4 w-4 animate-spin inline" />
                      </td>
                    );
                  }

                  if (!costForQty) {
                    return (
                      <td
                        key={qty}
                        className="py-2 px-4 text-right border-l border-border border-t-2 border-t-border"
                      >
                        —
                      </td>
                    );
                  }

                  const unitMargin = new Decimal(
                    sumMargins(costForQty.marginsByType),
                  );
                  const totalMargin = unitMargin.times(qty).toNumber();

                  return (
                    <td
                      key={qty}
                      className="py-2 px-4 text-right text-sm border-l border-border border-t-2 border-t-border tabular-nums"
                    >
                      <div>
                        $
                        {unitMargin.toNumber().toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 3,
                        })}
                        /ea
                      </div>
                      <div className="text-[10px] text-muted-foreground font-normal">
                        $
                        {totalMargin.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 3,
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <MarginEditorSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        margins={margins}
        costingMethod={costingMethod}
        onSave={handleSaveMargins}
      />
    </>
  );
}
